import React, { useEffect, useContext, Fragment } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { BusinessUnit } from '@zola-helpers/client/dist/es/@types';
import detectBusinessUnit from '@zola-helpers/client/dist/es/util/detectBusinessUnit';

// import confettiSVG from 'assets/images/navigation/confetti-mini.svg';

import { NAV_SECTIONS } from 'util/trackNavigationClickedHelpers';
import TextUtil from 'util/TextUtil';
import { trackNavigationClicked } from 'util/trackingHelper';
import hasChildren from 'util/hasChildren';

import NavContext from 'components/navV2/NavScrollContainer/context';
import NavLink from 'components/navV2/NavLink';
import NavDropdown from 'components/navV2/NavDropdown';
import { isLoggedIn } from 'util/authHelpers';
import NavAccount from 'components/navV2/NavAccount';
import { UniversalTabs } from 'reducers/nav';
import UniversalTabList from '../UniversalTabList/UniversalTabList';

import { PromoContainer, PromoText } from './PromotionalBar.styles';

import './PromotionalBar.less';

export const PromotionalBarComponent = ({
  promotionalBarData,
  showMessage,
  onShowLogin,
  handleRequestPromoMessage,
  message,
  link,
  promoBackgroundColor,
  promoTextColor,
  busy,
  messageLoaded,
  userContext,
  accountNavData,
  activeUniversalTab,
  currentTab,
  isNavV3,
}) => {
  useEffect(() => {
    // Temporarily skip check for prop showMessage and show this message to all users.  Re-add below when ready.
    // if (!messageLoaded && showMessage && !busy) {
    if (!messageLoaded && !busy) {
      handleRequestPromoMessage();
    }
  }, [messageLoaded, showMessage, busy, handleRequestPromoMessage]);
  const { hidePromoSection } = useContext(NavContext);
  let showWeddingsAccount = activeUniversalTab === UniversalTabs.WEDDINGS;
  if (currentTab === 'HOME_STORE') showWeddingsAccount = false;
  const classes = cx('navV2__promotionalBar--container', 'nav-section', 'nav-section-top', {
    'nav-section--hide': hidePromoSection,
  });

  const onClick = (child, i, isSubMenu) => {
    const eventData = {
      menu_item: TextUtil.toAllUpperCase(child.title),
      section: isSubMenu ? NAV_SECTIONS.promotionalSubNav : NAV_SECTIONS.promotional,
      node_level_1: TextUtil.toAllUpperCase(child.title),
      // Row and column position are 1-indexed.
      row_position: isSubMenu ? i + 1 : 1,
      column_position: isSubMenu ? 1 : i + 1,
    };
    trackNavigationClicked(eventData);
  };

  const isHomeStore = detectBusinessUnit() === BusinessUnit.STORE;
  const homeStoreLoginHandler = () => {
    window.location.href = '/account/login';
  };

  const renderWeddingsAccountLinks = () => {
    const loginHandler = isHomeStore ? homeStoreLoginHandler : onShowLogin;

    return isLoggedIn(userContext) ? (
      <li className="navV2__promotionalBar--nav-item" key="account-nav-key">
        <NavAccount accountNavData={accountNavData} />
      </li>
    ) : (
      <li className="navV2__promotionalBar--nav-item" key="login-nav-key">
        <button
          onClick={loginHandler}
          className="nav-login nav-login-button  nav-link nav-link--PromoNavLink"
          type="button"
        >
          Log In
        </button>
      </li>
    );
  };

  const renderHomeStoreAccountLinks = () => {
    const logInClickHandler = isHomeStore ? homeStoreLoginHandler : onShowLogin;
    return isLoggedIn(userContext) ? (
      <li className="navV2__promotionalBar--nav-item" key="account-nav-key">
        <NavAccount accountNavData={accountNavData} />
      </li>
    ) : (
      <li className="navV2__promotionalBar--nav-item" key="login-nav-key">
        <NavDropdown
          label="Log In"
          ariaLabel="Log In"
          menuPositionX="right"
          eventType="hover"
          className="navV2__promotionalBar--nav-dropdown"
          onClick={logInClickHandler}
        >
          <ul>
            <li className="advice-dropdown--item" key="home-store-log-in">
              <NavLink variant="PromoNavSubLink" href="/account/login" className="nav-login">
                Log In
              </NavLink>
            </li>
            <li className="advice-dropdown--item" key="home-store-create-account">
              <NavLink variant="PromoNavSubLink" href="/account/new">
                Create Account
              </NavLink>
            </li>
          </ul>
        </NavDropdown>
      </li>
    );
  };

  const renderRightLinks = () => {
    const promoLinks = promotionalBarData.map((item, i) => {
      if (!hasChildren(item)) {
        return (
          <li className="navV2__promotionalBar--nav-item" key={i}>
            <NavLink
              variant="PromoNavLink"
              href={item.path}
              onClick={() => onClick(item, i, false)}
            >
              {item.title}
            </NavLink>
          </li>
        );
      }
      return (
        <li className="navV2__promotionalBar--nav-item" key={i}>
          <NavDropdown
            label={item.title}
            ariaLabel={item.title}
            link={{ href: item.path }}
            menuPositionX="right"
            eventType="hover"
            className={`navV2__promotionalBar--${item.id}-dropdown navV2__promotionalBar--nav-dropdown`}
          >
            <ul>
              {item.children.map((child, idx) => (
                <li className="advice-dropdown--item" key={idx}>
                  <NavLink
                    variant="PromoNavSubLink"
                    href={child.path}
                    onClick={() => onClick(child, idx, true)}
                  >
                    {child.title}
                  </NavLink>
                </li>
              ))}
            </ul>
          </NavDropdown>
        </li>
      );
    });

    return (
      <Fragment>
        {promoLinks}
        {showWeddingsAccount ? renderWeddingsAccountLinks() : renderHomeStoreAccountLinks()}
      </Fragment>
    );
  };

  const renderPromoMessage = () => {
    return link ? (
      <div className="navV2__promotionalBar--promo-container">
        {/* <img src={confettiSVG} alt="" className="navV2__promotionalBar--confetti" /> */}
        <PromoText color={promoTextColor} sizes="smaller" href={link} inline noUnderline>
          <div
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: message }}
          />
        </PromoText>
      </div>
    ) : null;
  };

  return (
    <PromoContainer className={classes} backgroundColor={promoBackgroundColor}>
      {isNavV3 ? (
        renderPromoMessage()
      ) : (
        <Fragment>
          <UniversalTabList />
          {renderPromoMessage()}
          <ul className="navV2__promotionalBar--links-container navV2__promotionalBar--links-container-right">
            {renderRightLinks()}
          </ul>
        </Fragment>
      )}
    </PromoContainer>
  );
};

PromotionalBarComponent.propTypes = {
  userContext: PropTypes.shape({}),
  showMessage: PropTypes.bool,
  handleRequestPromoMessage: PropTypes.func,
  message: PropTypes.string,
  link: PropTypes.string,
  promoBackgroundColor: PropTypes.string,
  promoTextColor: PropTypes.string,
  activeUniversalTab: PropTypes.string,
  busy: PropTypes.bool,
  messageLoaded: PropTypes.bool,
  promotionalBarData: PropTypes.arrayOf(PropTypes.object),
  accountNavData: PropTypes.shape({}),
  onShowLogin: PropTypes.func,
  currentTab: PropTypes.string,
  // eslint-disable-next-line react/no-unused-prop-types
  activeLinkId: PropTypes.string,
  isNavV3: PropTypes.bool,
};

PromotionalBarComponent.defaultProps = {
  promotionalBarData: [],
  userContext: {},
};

export default PromotionalBarComponent;
